import { z } from "zod"

import { FreightTerms, Incoterms } from "@/graphql/codegen/graphql"

const tierSchema = z.object({
  uomId: z.string().min(1, "Pricing UoM is required"),
  qty: z
    .number({ invalid_type_error: "Product Quantity is required" })
    .positive("Product Quantity must be a positive number")
    .nullable()
    .refine((value) => value !== null, { message: "Product Quantity is required" }),
  price: z
    .number({ invalid_type_error: "Price per Unit is required" })
    .positive("Price per Unit must be a positive number")
    .nullable()
    .refine((value) => value !== null, { message: "Price per Unit is required" }),
})

export const purchaseCostFormSchema = z
  .object({
    warehouseAreaId: z.string().min(1, "Warehouse is required"),
    tiers: z.array(tierSchema).min(1, "At least one price tier is required"),
    effectiveDate: z.string().min(1, "Effective Date is required"),
    expirationDate: z.string().nullable(),
    freightTerms: z
      .union([z.nativeEnum(FreightTerms), z.literal("")])
      .refine((value) => value !== "", { message: "Freight is required" }),
    incoterms: z
      .union([z.nativeEnum(Incoterms), z.literal("")])
      .refine((value) => value !== "", { message: "Incoterms is required" }),
    incotermsLocationId: z.string().min(1, "Incoterms Location is required"),
  })
  .superRefine((data, ctx) => {
    for (let index = 1; index < data.tiers.length; index++) {
      const current = data.tiers[index].qty
      const previous = data.tiers[index - 1].qty
      if (current === null || previous === null) continue
      if (current <= previous) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Pricing tiers must be in increasing order of quantity",
          path: ["tiers", index, "qty"],
        })
      }
    }
  })
  .refine(
    (data) => {
      if (!data.expirationDate) return true
      return data.expirationDate >= data.effectiveDate
    },
    {
      message: "Valid Until Date must be on or after Effective Date",
      path: ["expirationDate"],
    }
  )

export type PurchaseCostFormValues = z.input<typeof purchaseCostFormSchema>

export const PURCHASE_COST_DEFAULT_FORM_VALUES: PurchaseCostFormValues = {
  warehouseAreaId: "",
  tiers: [{ uomId: "", qty: null, price: null }],
  effectiveDate: "",
  expirationDate: null,
  freightTerms: "",
  incoterms: "",
  incotermsLocationId: "",
}
